import React, { useEffect, useState, useMemo } from 'react';
import { Table, TableProps, Select, Row, Col, PaginationProps, Button, Modal, Space, Alert, Typography, Tooltip, Input, Tag } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, SearchOutlined, CalendarOutlined, SyncOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { getRequest, ResponseDataType, postRequest, putRequest, deleteRequest } from 'api';
import { toast } from 'react-toastify';
import { StudentFullInfo } from 'Pages/Revenue';
import { EllipsisTooltip } from './EllipsisTooltip';
import { ClassLevelSelect } from 'Components';
import { getStoredWorkMonth } from 'utils/workMonthStorage';

const { Option } = Select;

interface PendingBillDataType {
  invoidId: string;
  billId?: string;
  studentInvoiceId: number;
  studentId: number;
  studentName: string;
  className: string;
  createdDate: number;
  parentPhonenum: string;
  cost: number;
  content: string;
  description: string;
  status: number;
  paymentDate: string | null;
  updatedDate: number;
  schoolYear: string;
  month: string;
  year: string;
  studentCode: string;
  classYearName: string;
  classYearId: number;
}

const formatCurrency = (value: number): string => {
  const formattedValue = new Intl.NumberFormat('vi-VN').format(value);
  return `${formattedValue} VNĐ`;
};

export const PendingApprovalBillList = () => {
  const [pendingBillList, setPendingBillList] = useState<PendingBillDataType[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(() => getStoredWorkMonth().year);
  const [selectedMonth, setSelectedMonth] = useState<number>(() => getStoredWorkMonth().month);
  const [selectedClass, setSelectedClass] = useState<number | undefined>(undefined);
  const [selectedClassLevel, setSelectedClassLevel] = useState<string | undefined>(undefined);
  const [classList, setClassList] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedStudentForDetail, setSelectedStudentForDetail] = useState<PendingBillDataType | undefined>();
  const [fetchedStudentDetails, setFetchedStudentDetails] = useState<any>(null);
  const [searchStudentName, setSearchStudentName] = useState<string>('');
  const [debouncedStudentName, setDebouncedStudentName] = useState<string>('');

  const years = useMemo(() => {
    const startYear = 2021;
    const endYear = Math.max(new Date().getFullYear() + 2, selectedYear + 1);
    const list: number[] = [];
    for (let y = startYear; y <= endYear; y++) {
      list.push(y);
    }
    return list;
  }, [selectedYear]);
  const months = [
    { label: 'Tháng 1', value: 1 },
    { label: 'Tháng 2', value: 2 },
    { label: 'Tháng 3', value: 3 },
    { label: 'Tháng 4', value: 4 },
    { label: 'Tháng 5', value: 5 },
    { label: 'Tháng 6', value: 6 },
    { label: 'Tháng 7', value: 7 },
    { label: 'Tháng 8', value: 8 },
    { label: 'Tháng 9', value: 9 },
    { label: 'Tháng 10', value: 10 },
    { label: 'Tháng 11', value: 11 },
    { label: 'Tháng 12', value: 12 },
  ];
  const schoolId = localStorage.getItem('schoolId');

  const fetchPendingBills = async () => {
    setLoading(true);
    const queryParams: Record<string, string> = {
      page: currentPage.toString(),
      perPage: pageSize.toString(),
      month: selectedMonth.toString(),
      year: selectedYear.toString(),
      schoolId: schoolId || '',
      status: '-1',
    };
    if (selectedClass) {
      queryParams['classId'] = selectedClass.toString();
    } else if (selectedClassLevel) {
      queryParams['classLevel'] = selectedClassLevel;
    }
    if (debouncedStudentName) {
      queryParams['studentName'] = debouncedStudentName;
    }
    const queryString = new URLSearchParams(queryParams).toString();

    const url = `/cms/invoice/list-invoices?${queryString}`;
    const response: ResponseDataType<{ list: PendingBillDataType[], total: number }> = await getRequest(url);

    if (response?.code === 200) {
      setPendingBillList(response.info?.list || []);
      setTotalItems(response.info?.total || 0);
    } else {
      setPendingBillList([]);
      console.log(response?.msg || 'Lỗi không xác định');
    }
    setLoading(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedStudentName(searchStudentName.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchStudentName]);

  const handlePressEnter = () => {
    setDebouncedStudentName(searchStudentName.trim());
    setCurrentPage(1);
  };

  // Lắng nghe thay đổi Kỳ làm việc từ cấu hình / thanh Header
  useEffect(() => {
    const handleWorkMonthChange = (e: any) => {
      if (e.detail) {
        setSelectedMonth(e.detail.month);
        setSelectedYear(e.detail.year);
        setCurrentPage(1);
      }
    };
    window.addEventListener('workMonthChanged', handleWorkMonthChange);
    return () => window.removeEventListener('workMonthChanged', handleWorkMonthChange);
  }, []);

  useEffect(() => {
    fetchPendingBills();
    setSelectedRowKeys([]);
  }, [selectedYear, selectedMonth, currentPage, pageSize, selectedClass, selectedClassLevel, debouncedStudentName]);

  useEffect(() => {
    const fetchClasses = async () => {
      if (!schoolId) return;
      const url = `/cms/class/get-class-years-by-start-end-schoolId?schoolId=${schoolId}&start=${selectedYear}&page=1&perPage=1000`;
      const res: ResponseDataType<any> = await getRequest(url);
      if (res?.code === 200) {
        setClassList(res?.info?.list || []);
      }
    };
    fetchClasses();
  }, [schoolId, selectedYear]);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleApprove = (invoidId: string) => {
    Modal.confirm({
      title: 'Xác nhận duyệt biên lai',
      content: `Bạn có chắc chắn muốn duyệt biên lai này không?`,
      okText: 'Duyệt',
      cancelText: 'Hủy',
      async onOk() {
        try {
          const res = await putRequest(`/cms/accountant/income-student-register/group/invoice/approve`, [invoidId]);
          const result = Array.isArray(res) ? res[0] : res;
          if (result?.code == 200) {
            toast.success('Duyệt biên lai thành công');
            fetchPendingBills();
          } else {
            toast.error(result?.msg || 'Lỗi khi duyệt biên lai');
          }
        } catch (error) {
          console.error('Error approving invoice:', error);
          toast.error('Lỗi hệ thống khi duyệt biên lai');
        }
      }
    });
  };

  const handleReject = (invoidId: string) => {
    Modal.confirm({
      title: 'Xác nhận không duyệt biên lai',
      content: `Biên lai này sẽ bị xóa khỏi danh sách chờ duyệt. Bạn có chắc chắn không?`,
      okText: 'Không duyệt',
      okType: 'danger',
      cancelText: 'Hủy',
      async onOk() {
        try {
          const res = await deleteRequest(`/cms/accountant/income-student-register/group/invoice`, [invoidId]);

          const result = Array.isArray(res) ? res[0] : res;
          if (result?.code == 200) {
            toast.success('Đã từ chối duyệt biên lai');
            fetchPendingBills();
          } else {
            toast.error(result?.msg || 'Lỗi khi từ chối biên lai');
          }
        } catch (error) {
          console.error('Error rejecting invoice:', error);
          toast.error('Lỗi hệ thống khi từ chối biên lai');
        }
      }
    });
  };

  const handleBulkApprove = () => {
    if (selectedRowKeys.length === 0) return;
    Modal.confirm({
      title: 'Xác nhận duyệt biên lai hàng loạt',
      content: `Bạn có chắc chắn muốn duyệt ${selectedRowKeys.length} biên lai đã chọn không?`,
      okText: 'Duyệt',
      cancelText: 'Hủy',
      async onOk() {
        try {
          const selectedInvoidIds = pendingBillList
            .filter(item => selectedRowKeys.includes(item.studentInvoiceId))
            .map(item => item.invoidId || item.billId || '');

          const res = await putRequest(`/cms/accountant/income-student-register/group/invoice/approve`, selectedInvoidIds);

          const result = Array.isArray(res) ? res[0] : res;
          if (result?.code == 200) {
            toast.success(`Đã duyệt thành công ${selectedRowKeys.length} biên lai.`);
            setSelectedRowKeys([]);
            fetchPendingBills();
          } else {
            toast.error(result?.msg || 'Lỗi khi duyệt biên lai hàng loạt');
          }
        } catch (error) {
          console.error('Error bulk approving invoices:', error);
          toast.error('Lỗi hệ thống khi duyệt biên lai hàng loạt');
        }
      }
    });
  };

  const handleBulkReject = () => {
    if (selectedRowKeys.length === 0) return;
    Modal.confirm({
      title: 'Xác nhận không duyệt biên lai hàng loạt',
      content: `Các biên lai này sẽ bị xóa khỏi danh sách chờ duyệt. Bạn có chắc chắn không duyệt ${selectedRowKeys.length} biên lai đã chọn?`,
      okText: 'Không duyệt',
      okType: 'danger',
      cancelText: 'Hủy',
      async onOk() {
        try {
          const selectedInvoidIds = pendingBillList
            .filter(item => selectedRowKeys.includes(item.studentInvoiceId))
            .map(item => item.invoidId || item.billId || '');

          const res = await deleteRequest(`/cms/accountant/income-student-register/group/invoice`, selectedInvoidIds);

          const result = Array.isArray(res) ? res[0] : res;
          if (result?.code == 200) {
            toast.success(`Đã từ chối duyệt ${selectedRowKeys.length} biên lai.`);
            setSelectedRowKeys([]);
            fetchPendingBills();
          } else {
            toast.error(result?.msg || 'Lỗi khi từ chối biên lai hàng loạt');
          }
        } catch (error) {
          console.error('Error bulk rejecting invoices:', error);
          toast.error('Lỗi hệ thống khi từ chối biên lai hàng loạt');
        }
      }
    });
  };

  const columns: TableProps<PendingBillDataType>['columns'] = useMemo(() => [
    {
      title: 'Mã biên lai',
      dataIndex: 'invoidId',
      key: 'invoidId',
      align: 'center',
      className: 'cell-center',
      width: 140,
      ellipsis: {
        showTitle: false,
      },
      render: (_: any, record: PendingBillDataType) => (
        <EllipsisTooltip text={record?.invoidId || record?.billId || ''} />
      ),
    },
    {
      title: 'CCCD',
      dataIndex: 'studentCode',
      key: 'studentCode',
      align: 'center',
      className: 'cell-center',
      width: 135,
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => <EllipsisTooltip text={text} />,
    },
    {
      title: 'Tên học sinh',
      dataIndex: 'studentName',
      key: 'studentName',
      align: 'left',
      width: 190,
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => <EllipsisTooltip text={text} />,
    },
    {
      title: 'Lớp học',
      dataIndex: 'className',
      key: 'className',
      render: (_: any, record: PendingBillDataType) => (
        <EllipsisTooltip text={record?.classYearName || ''} />
      ),
      align: 'center',
      className: 'cell-center',
      width: 110,
      ellipsis: {
        showTitle: false,
      },
    },
    {
      title: 'Số tiền',
      dataIndex: 'cost',
      key: 'cost',
      render: (cost: number) => <EllipsisTooltip text={formatCurrency(cost)} />,
      align: 'center',
      className: 'cell-center',
      width: 130,
      ellipsis: {
        showTitle: false,
      },
    },
    {
      title: 'Nội dung',
      dataIndex: 'content',
      key: 'content',
      align: 'left',
      ellipsis: {
        showTitle: false,
      },
      render: (content: string) => <EllipsisTooltip text={content} />,
    },
    {
      title: 'Hành động',
      key: 'action',
      width: 230,
      align: 'center',
      className: 'cell-center',
      render: (_, record) => (
        <Space size="middle" style={{ whiteSpace: 'nowrap' }}>
          <span
            onClick={() => handleApprove(record.invoidId || record.billId || '')}
            style={{ color: '#52c41a', cursor: 'pointer', fontWeight: 500 }}
          >
            Duyệt
          </span>
          <span
            onClick={() => handleReject(record.invoidId || record.billId || '')}
            style={{ color: '#ff4d4f', cursor: 'pointer', fontWeight: 500 }}
          >
            Không duyệt
          </span>
          <span
            onClick={() => {
              setSelectedStudentForDetail(record);
              setIsDetailModalOpen(true);
            }}
            style={{ color: '#1890ff', cursor: 'pointer', fontWeight: 500 }}
          >
            Sửa
          </span>
        </Space>
      ),
    }
  ], []);

  const handlePageChange: PaginationProps['onChange'] = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize || 10);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px' }}>
        <Select
          placeholder="Năm"
          value={selectedYear}
          onChange={(value) => setSelectedYear(value)}
          style={{ width: 120 }}
        >
          {years.map((year) => (
            <Option key={year} value={year}>
              {year}
            </Option>
          ))}
        </Select>
        <Select
          placeholder="Tháng"
          value={selectedMonth}
          onChange={(value) => setSelectedMonth(value)}
          style={{ width: 120 }}
        >
          {months.map((month) => (
            <Option key={month.value} value={month.value}>
              {month.label}
            </Option>
          ))}
        </Select>
        {selectedMonth === getStoredWorkMonth().month && selectedYear === getStoredWorkMonth().year ? (
          <Tag color="blue" style={{ height: 32, display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 500, margin: 0 }}>
            <CalendarOutlined /> Kỳ làm việc
          </Tag>
        ) : (
          <Tooltip title="Đang xem kỳ khác. Bấm để quay về Kỳ làm việc trên thanh Header">
            <Button
              size="middle"
              icon={<SyncOutlined />}
              onClick={() => {
                const wm = getStoredWorkMonth();
                setSelectedMonth(wm.month);
                setSelectedYear(wm.year);
                setCurrentPage(1);
              }}
            >
              Về kỳ T{getStoredWorkMonth().month}/{getStoredWorkMonth().year}
            </Button>
          </Tooltip>
        )}
        <ClassLevelSelect
          value={selectedClassLevel}
          onChange={(val) => {
            setSelectedClassLevel(val);
            setSelectedClass(undefined);
            setCurrentPage(1);
          }}
          placeholder="Tất cả khối"
          style={{ width: 130 }}
        />
        <Select
          showSearch
          allowClear
          placeholder="Lớp học"
          value={selectedClass}
          onChange={(value) => {
            setSelectedClass(value);
            setCurrentPage(1); // Reset page when class changes
          }}
          style={{ width: 180 }}
          filterOption={(input, option) =>
            (option?.children as unknown as string)
              ?.toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {classList
            .filter((cls) => !selectedClassLevel || String(cls.classLevel).trim() === selectedClassLevel.trim())
            .map((cls) => (
              <Option key={cls.classYearId} value={cls.classYearId}>
                {cls.className}
              </Option>
            ))}
        </Select>
        <Input
          placeholder="Tìm tên học sinh"
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          allowClear
          value={searchStudentName}
          onChange={(e) => setSearchStudentName(e.target.value)}
          onPressEnter={handlePressEnter}
          style={{ width: 180 }}
        />
      </div>

      {selectedRowKeys.length > 0 && (
        <div style={{
          marginBottom: 16,
          padding: '10px 20px',
          backgroundColor: '#fff',
          border: '1px solid #e8e8e8',
          borderRadius: '10px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Space size="middle">
            <Typography.Text style={{ color: '#595959', fontSize: '15px' }}>
              Đã chọn <Typography.Text strong style={{ color: '#1890ff' }}>{selectedRowKeys.length}</Typography.Text> biên lai
            </Typography.Text>
          </Space>

          <Space size="small">
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={handleBulkApprove}
              style={{
                backgroundColor: '#52c41a',
                borderColor: '#52c41a',
                borderRadius: '6px',
                height: '34px',
                fontWeight: '500'
              }}
            >
              Duyệt biên lai
            </Button>
            <Button
              danger
              type="text"
              icon={<CloseCircleOutlined />}
              onClick={handleBulkReject}
              style={{
                height: '34px',
                fontWeight: '500',
                borderRadius: '6px'
              }}
            >
              Từ chối duyệt
            </Button>
          </Space>
        </div>
      )}

      <Table
        tableLayout="fixed"
        rowSelection={rowSelection}
        columns={columns}
        dataSource={pendingBillList}
        rowKey="studentInvoiceId"
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalItems,
          pageSizeOptions: ['10', '50', '100', '200', '500'],
          showSizeChanger: true,
          onChange: handlePageChange,
        }}
      />

      <Modal
        title="Chi tiết nguồn thu học sinh"
        open={isDetailModalOpen}
        onCancel={() => {
          setIsDetailModalOpen(false);
          setSelectedStudentForDetail(undefined);
          setFetchedStudentDetails(null);
        }}
        footer={null}
        width={1500}
        destroyOnClose
      >
        <div style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {selectedStudentForDetail && (
            <>
              <div style={{ marginBottom: 16, fontSize: 15 }}>
                <strong>Họ tên:</strong> {selectedStudentForDetail.studentName} <span style={{ margin: '0 8px', color: '#ccc' }}>|</span>
                <strong>Lớp:</strong> {selectedStudentForDetail.classYearName} <span style={{ margin: '0 8px', color: '#ccc' }}>|</span>
                <strong>Ngày sinh:</strong> {fetchedStudentDetails?.dob || ''} <span style={{ margin: '0 8px', color: '#ccc' }}>|</span>
                <strong>CMND/CCCD:</strong> {selectedStudentForDetail.studentCode}
              </div>
              <StudentFullInfo
                studentId={selectedStudentForDetail.studentId}
                classYearId={selectedStudentForDetail.classYearId}
                status={1}
                onStudentLoaded={(student) => setFetchedStudentDetails(student)}
              />
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};
